import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { ErrorOutline as ErrorOutlineIcon } from '@mui/icons-material';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /**
   * When this value changes, the boundary clears any captured error and
   * re-renders its children. Pass the current route path to get per-route
   * recovery: navigating elsewhere resets the boundary automatically.
   */
  resetKey?: string | number;
  /** Optional label describing the area, shown in the fallback copy. */
  title?: string;
  /** Full-page variant (used for the top-level app boundary). */
  fullPage?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Catches render/runtime errors in its subtree so a single broken page cannot
 * white-screen the whole application. Offers in-place recovery.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    // Per-route recovery: reset when the resetKey changes (e.g. route change).
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { title, fullPage } = this.props;

    return (
      <Box
        role="alert"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          gap: 2,
          p: 4,
          minHeight: fullPage ? '100vh' : '40vh',
        }}
      >
        <ErrorOutlineIcon color="error" sx={{ fontSize: 48 }} />
        <Typography variant="h5">Something went wrong</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480 }}>
          {title
            ? `The ${title} page hit an unexpected error and couldn't be displayed.`
            : "This part of the app hit an unexpected error and couldn't be displayed."}{' '}
          You can try again, or reload the page.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button variant="contained" onClick={this.handleReset}>
            Try again
          </Button>
          <Button variant="outlined" onClick={this.handleReload}>
            Reload page
          </Button>
        </Stack>
      </Box>
    );
  }
}

export default ErrorBoundary;
